import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToUserWallet,
  listenToUserSavedCards,
  cleanupWalletListeners,
} from "../../redux/transactionsSlice";
import { calculateTopUpFees, resetTopUpFees } from "../../redux/walletServiceFeesSlice";
import { MAIN_CURRENCIES, ALL_CURRENCIES } from "../../GLOBAL/Currencies";
import axios from "axios";
import logo from "../../assets/cosmoola/logo.jpg";
import {
  ArrowLeft,
  Plus,
  Minus,
  CreditCard,
  Wallet,
  CheckCircle,
  AlertCircle,
  Shield,
  ChevronDown,
  ChevronUp,
  Loader2,
  Eye,
  EyeOff,
  X,
} from "lucide-react";

/* ─── THEME ─────────────────────────────────────────────── */
const D = { bg:"#000000", surface:"#0a0a0a", surface2:"#101010", border:"#1c1c1c", border2:"#2a2a2a", white:"#ffffff", offwhite:"#e8e8e8", muted:"#6b6b6b", subtle:"#3a3a3a", gold:"#C9A96E" };
const L = { bg:"#ffffff", surface:"#fafafa", surface2:"#f3f3f3", border:"#e5e5e5", border2:"#d4d4d4", text:"#0a0a0a", textSoft:"#525252", muted:"#a3a3a3", subtle:"#d4d4d4" };

const QUICK_AMOUNTS = [10, 25, 50, 100, 250, 500, 1000, 5000];

const fmt = (n: number, sym = "€") => sym + Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 });

/* ═══════════════════════════════════════════════════════════ */
/*                       ADD MONEY PAGE                       */
/* ═══════════════════════════════════════════════════════════ */

const AddMoney: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((s: RootState) => s.auth);
  const userId = user?.userId ?? "";
  const { wallet, savedCards } = useSelector((s: RootState) => s.transactions);
  const { serviceFee, totalCharged, feePercentage, feesCalculated } = useSelector((s: RootState) => s.walletServiceFees);

  const [amount, setAmount] = useState(50);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState("EUR");
  const [showAllCurrencies, setShowAllCurrencies] = useState(false);
  const [balanceHidden, setBalanceHidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [loaded, setLoaded] = useState(false);

  const currencySymbol = useMemo(() => {
    const c = ALL_CURRENCIES.find((c) => c.code === selectedCurrency);
    return c?.symbol ?? "€";
  }, [selectedCurrency]);

  /* ─── LISTENERS ─── */
  useEffect(() => {
    if (userId) {
      dispatch(listenToUserWallet(userId));
      dispatch(listenToUserSavedCards(userId));
    }
    return () => { dispatch(cleanupWalletListeners()); };
  }, [userId, dispatch]);

  /* ─── AUTO-SELECT DEFAULT CARD ─── */
  useEffect(() => {
    if (savedCards.length && !selectedCardId) {
      const def = savedCards.find((c) => c.isDefault);
      setSelectedCardId(def?.id ?? savedCards[0].id);
    }
  }, [savedCards, selectedCardId]);

  /* ─── CALCULATE FEES ON AMOUNT CHANGE ─── */
  useEffect(() => {
    if (amount >= 10) {
      dispatch(calculateTopUpFees({ amount }));
    }
  }, [amount, dispatch]);

  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  /* ─── AMOUNT CONTROLS ─── */
  const increment = (step: number) => setAmount((a) => Math.min(1000000, Math.max(10, a + step)));
  const decrement = (step: number) => setAmount((a) => Math.max(10, a - step));

  /* ─── TOP UP ─── */
  const handleTopUp = async () => {
    if (!selectedCardId || amount < 10) return;
    setLoading(true);
    setResult(null);

    try {
      const res = await axios.post(
        "https://europe-west3-cosmoola.cloudfunctions.net/topUpWallet",
        { userId, cardId: selectedCardId, amount }
      );

      if (res.data.success) {
        setResult({ success: true, message: `${fmt(amount, currencySymbol)} added to your wallet` });
        setAmount(50);
        dispatch(resetTopUpFees());
      } else {
        setResult({ success: false, message: res.data.error || "Top-up failed" });
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || "Something went wrong";
      setResult({ success: false, message: msg });
    } finally {
      setLoading(false);
    }
  };

  const balance = wallet?.balance ?? 0;

  return (
    <div style={{ minHeight: "100vh", background: L.bg, color: L.text, fontFamily: "'Montserrat','Cormorant Garamond',system-ui,sans-serif" }}>
      <style>{ADD_MONEY_CSS}</style>

      {/* ── HEADER ── */}
      <header style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(0,0,0,0.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid " + D.border, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", height: 58 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: D.offwhite, display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.05em" }}>
            <ArrowLeft size={16} strokeWidth={1.5} />
            <span className="back-label">BACK</span>
          </button>
        </div>
        <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
          <span style={{ color: D.white, fontWeight: 300, letterSpacing: "0.35em", fontFamily: "'Cormorant Garamond',serif", fontSize: 15 }}>COSMOOLA</span>
        </div>
        <img src={logo} alt="C" style={{ width: 28, height: 28, borderRadius: 0, objectFit: "cover" }} />
      </header>

      {/* ── CONTENT ── */}
      <div className="am-container">

        {/* ── LEFT / MAIN COLUMN ── */}
        <div className="am-main">

          {/* TITLE */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 16 }} transition={{ duration: 0.4 }} style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 28, fontWeight: 300, color: L.text, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.06em", margin: 0 }}>Add Money</h1>
            <p style={{ fontSize: 12, color: L.textSoft, marginTop: 6, letterSpacing: "0.05em" }}>Top up your Cosmoola wallet</p>
          </motion.div>

          {/* BALANCE CARD */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 16 }} transition={{ duration: 0.4, delay: 0.05 }} style={{ background: D.bg, border: "1px solid " + D.border2, padding: "24px 22px", marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <p style={{ fontSize: 9, color: D.muted, letterSpacing: "0.25em", marginBottom: 8 }}>CURRENT BALANCE</p>
                <p style={{ fontSize: 32, fontWeight: 300, color: D.white, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "-0.02em" }}>
                  {balanceHidden ? "€ •••••" : fmt(balance)}
                </p>
              </div>
              <button onClick={() => setBalanceHidden((v) => !v)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                {balanceHidden ? <Eye size={16} color={D.muted} /> : <EyeOff size={16} color={D.muted} />}
              </button>
            </div>
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid " + D.border, display: "flex", alignItems: "center", gap: 8 }}>
              <Shield size={12} color={D.gold} />
              <span style={{ fontSize: 10, color: D.muted, letterSpacing: "0.1em" }}>SECURED BY STRIPE</span>
            </div>
          </motion.div>

          {/* CURRENCY SELECTOR */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 16 }} transition={{ duration: 0.4, delay: 0.1 }} style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", marginBottom: 12 }}>CURRENCY</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {MAIN_CURRENCIES.map((c) => (
                <motion.button
                  key={c.code}
                  onClick={() => setSelectedCurrency(c.code)}
                  whileTap={{ scale: 0.95 } as any}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "8px 16px",
                    borderRadius: 100,
                    border: selectedCurrency === c.code ? "2px solid " + L.text : "1px solid " + L.border,
                    background: selectedCurrency === c.code ? L.text : "transparent",
                    color: selectedCurrency === c.code ? L.bg : L.text,
                    cursor: "pointer", fontSize: 11, fontWeight: 500, fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.05em",
                  }}
                >
                  <span style={{ fontSize: 14 }}>{c.flag}</span>
                  {c.code}
                </motion.button>
              ))}
              <motion.button
                onClick={() => setShowAllCurrencies((v) => !v)}
                whileTap={{ scale: 0.95 } as any}
                style={{
                  display: "flex", alignItems: "center", gap: 4,
                  padding: "8px 14px", borderRadius: 100,
                  border: "1px solid " + L.border, background: "transparent",
                  color: L.textSoft, cursor: "pointer", fontSize: 11, fontFamily: "'Montserrat',sans-serif",
                }}
              >
                {showAllCurrencies ? "Less" : "More"}
                {showAllCurrencies ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </motion.button>
            </div>

            <AnimatePresence>
              {showAllCurrencies && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  style={{ overflow: "hidden" }}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10, paddingTop: 10, borderTop: "1px solid " + L.border }}>
                    {ALL_CURRENCIES.filter((c) => !MAIN_CURRENCIES.some((m) => m.code === c.code)).map((c) => (
                      <motion.button
                        key={c.code}
                        onClick={() => { setSelectedCurrency(c.code); setShowAllCurrencies(false); }}
                        whileTap={{ scale: 0.95 } as any}
                        style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "7px 14px", borderRadius: 100,
                          border: selectedCurrency === c.code ? "2px solid " + L.text : "1px solid " + L.border,
                          background: selectedCurrency === c.code ? L.text : "transparent",
                          color: selectedCurrency === c.code ? L.bg : L.text,
                          cursor: "pointer", fontSize: 10, fontWeight: 500, fontFamily: "'Montserrat',sans-serif",
                        }}
                      >
                        <span style={{ fontSize: 13 }}>{c.flag}</span>
                        <span>{c.code}</span>
                        <span style={{ color: selectedCurrency === c.code ? L.surface : L.muted, fontSize: 9 }}>{c.name}</span>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* AMOUNT SELECTOR */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 16 }} transition={{ duration: 0.4, delay: 0.15 }} style={{ background: L.surface, border: "1px solid " + L.border, padding: "28px 22px", marginBottom: 20 }}>
            <p style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", marginBottom: 20 }}>AMOUNT</p>

            {/* Big amount display with +/- */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginBottom: 24 }}>
              <motion.button
                onClick={() => decrement(amount >= 1000 ? 100 : amount >= 100 ? 10 : 5)}
                whileTap={{ scale: 0.9 } as any}
                style={{ width: 48, height: 48, borderRadius: "50%", border: "1px solid " + L.border, background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: L.text }}
              >
                <Minus size={18} />
              </motion.button>

              <div style={{ textAlign: "center", minWidth: 180 }}>
                <div style={{ fontSize: 48, fontWeight: 300, color: L.text, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "-0.02em" }}>
                  {currencySymbol}{amount.toLocaleString("de-DE")}
                </div>
                <div style={{ fontSize: 10, color: L.muted, letterSpacing: "0.15em", marginTop: 4 }}>{selectedCurrency}</div>
              </div>

              <motion.button
                onClick={() => increment(amount >= 1000 ? 100 : amount >= 100 ? 10 : 5)}
                whileTap={{ scale: 0.9 } as any}
                style={{ width: 48, height: 48, borderRadius: "50%", border: "1px solid " + L.text, background: L.text, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: L.bg }}
              >
                <Plus size={18} />
              </motion.button>
            </div>

            {/* Quick amounts */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
              {QUICK_AMOUNTS.map((q) => (
                <motion.button
                  key={q}
                  onClick={() => setAmount(q)}
                  whileTap={{ scale: 0.95 } as any}
                  style={{
                    padding: "8px 16px", borderRadius: 0,
                    border: amount === q ? "1px solid " + L.text : "1px solid " + L.border,
                    background: amount === q ? L.text : "transparent",
                    color: amount === q ? L.bg : L.textSoft,
                    cursor: "pointer", fontSize: 11, fontWeight: 500, fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.05em",
                  }}
                >
                  {currencySymbol}{q.toLocaleString()}
                </motion.button>
              ))}
            </div>

            {/* Custom input */}
            <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, color: L.muted }}>Custom:</span>
              <input
                type="number"
                min={10}
                max={1000000}
                value={amount}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (!isNaN(v)) setAmount(Math.max(10, Math.min(1000000, v)));
                }}
                style={{
                  flex: 1, background: L.surface2, border: "1px solid " + L.border, borderRadius: 0,
                  padding: "10px 14px", color: L.text, fontSize: 14, fontFamily: "'Montserrat',sans-serif",
                  outline: "none", textAlign: "right",
                }}
              />
            </div>
          </motion.div>

          {/* SAVED CARDS */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 16 }} transition={{ duration: 0.4, delay: 0.2 }} style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <p style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em" }}>PAYMENT METHOD</p>
              <button onClick={() => navigate(`/PaymentMethods/${userId}`)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: L.muted, fontFamily: "inherit" }}>Manage →</button>
            </div>

            {savedCards.length === 0 ? (
              <div style={{ background: L.surface, border: "1px solid " + L.border, padding: "28px 22px", textAlign: "center" }}>
                <CreditCard size={28} color={L.muted} style={{ marginBottom: 12 }} />
                <p style={{ fontSize: 13, color: L.textSoft, marginBottom: 14 }}>No saved cards yet</p>
                <button
                  onClick={() => navigate(`/PaymentMethods/${userId}`)}
                  style={{ background: L.text, color: L.bg, border: "none", padding: "10px 24px", cursor: "pointer", fontSize: 11, fontWeight: 500, letterSpacing: "0.15em", fontFamily: "'Montserrat',sans-serif" }}
                >
                  ADD A CARD
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {savedCards.map((card) => (
                  <motion.button
                    key={card.id}
                    onClick={() => setSelectedCardId(card.id)}
                    whileTap={{ scale: 0.98 } as any}
                    style={{
                      display: "flex", alignItems: "center", gap: 14,
                      padding: "16px 18px",
                      background: selectedCardId === card.id ? L.surface : "transparent",
                      border: selectedCardId === card.id ? "2px solid " + L.text : "1px solid " + L.border,
                      cursor: "pointer", textAlign: "left", width: "100%", fontFamily: "inherit",
                    }}
                  >
                    {/* Radio */}
                    <div style={{
                      width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                      border: selectedCardId === card.id ? "6px solid " + L.text : "2px solid " + L.border,
                      background: L.bg,
                    }} />

                    {/* Card icon */}
                    <div style={{
                      width: 42, height: 30, borderRadius: 4, flexShrink: 0,
                      background: D.bg, border: "1px solid " + D.border2,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <CreditCard size={16} color={D.offwhite} />
                    </div>

                    {/* Card info */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: L.text }}>
                        {card.brand.toUpperCase()} •••• {card.last4}
                      </div>
                      <div style={{ fontSize: 10, color: L.muted, marginTop: 2 }}>
                        Expires {String(card.expMonth).padStart(2, "0")}/{card.expYear}
                        {card.isDefault && <span style={{ marginLeft: 8, color: L.text, fontWeight: 600 }}>DEFAULT</span>}
                      </div>
                    </div>

                    {selectedCardId === card.id && <CheckCircle size={16} color={L.text} />}
                  </motion.button>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* ── RIGHT / SUMMARY COLUMN ── */}
        <div className="am-summary">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 16 }}
            transition={{ duration: 0.4, delay: 0.25 }}
            style={{ background: L.surface, border: "1px solid " + L.border, padding: "28px 22px", position: "sticky", top: 80 }}
          >
            <p style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", marginBottom: 20 }}>ORDER SUMMARY</p>

            {/* Breakdown */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, color: L.textSoft }}>Wallet Credit</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: L.text }}>{fmt(amount, currencySymbol)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, color: L.textSoft }}>
                  Service Fee
                  <span style={{ fontSize: 10, color: L.muted, marginLeft: 4 }}>({feePercentage}%)</span>
                </span>
                <span style={{ fontSize: 13, color: L.textSoft }}>{fmt(serviceFee, currencySymbol)}</span>
              </div>
              <div style={{ height: 1, background: L.border }} />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: L.text }}>Total Charge</span>
                <span style={{ fontSize: 20, fontWeight: 300, color: L.text, fontFamily: "'Cormorant Garamond',serif" }}>
                  {fmt(feesCalculated ? totalCharged : amount, currencySymbol)}
                </span>
              </div>
            </div>

            {/* Selected card preview */}
            {selectedCardId && (() => {
              const card = savedCards.find((c) => c.id === selectedCardId);
              if (!card) return null;
              return (
                <div style={{ background: D.bg, border: "1px solid " + D.border2, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                  <CreditCard size={16} color={D.offwhite} />
                  <div>
                    <div style={{ fontSize: 11, color: D.offwhite, fontWeight: 500 }}>{card.brand.toUpperCase()} •••• {card.last4}</div>
                    <div style={{ fontSize: 9, color: D.muted, marginTop: 2 }}>Will be charged {fmt(feesCalculated ? totalCharged : amount, currencySymbol)}</div>
                  </div>
                </div>
              );
            })()}

            {/* ADD MONEY BUTTON */}
            <motion.button
              onClick={handleTopUp}
              disabled={loading || !selectedCardId || amount < 10}
              whileTap={{ scale: 0.97 } as any}
              style={{
                width: "100%",
                background: (!selectedCardId || amount < 10) ? L.border : D.bg,
                color: (!selectedCardId || amount < 10) ? L.muted : D.white,
                border: "none", padding: "16px 0",
                cursor: loading || !selectedCardId || amount < 10 ? "not-allowed" : "pointer",
                fontSize: 12, fontWeight: 600, letterSpacing: "0.2em",
                fontFamily: "'Montserrat',sans-serif",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? (
                <><Loader2 size={16} className="spin" /> PROCESSING...</>
              ) : (
                <><Wallet size={16} /> ADD MONEY</>
              )}
            </motion.button>

            {/* Min/Max note */}
            <p style={{ fontSize: 10, color: L.muted, textAlign: "center", marginTop: 12 }}>
              Min €10 · Max €1,000,000
            </p>

            {/* Security badge */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 16, paddingTop: 16, borderTop: "1px solid " + L.border }}>
              <Shield size={12} color={L.muted} />
              <span style={{ fontSize: 9, color: L.muted, letterSpacing: "0.1em" }}>256-BIT ENCRYPTED · PCI DSS COMPLIANT</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── RESULT TOAST ── */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
              background: result.success ? D.bg : "#B22222",
              color: D.white, padding: "16px 28px",
              display: "flex", alignItems: "center", gap: 12,
              border: result.success ? "1px solid " + D.gold : "1px solid #D44",
              zIndex: 9999, maxWidth: 500, width: "90%",
            }}
          >
            {result.success ? <CheckCircle size={20} color={D.gold} /> : <AlertCircle size={20} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{result.success ? "Top-Up Successful" : "Top-Up Failed"}</div>
              <div style={{ fontSize: 11, color: result.success ? D.muted : "rgba(255,255,255,0.7)", marginTop: 2 }}>{result.message}</div>
            </div>
            <button onClick={() => setResult(null)} style={{ background: "none", border: "none", cursor: "pointer", color: D.white, padding: 4 }}>
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── FOOTER ── */}
      <footer style={{ background: D.bg, borderTop: "1px solid " + D.border, padding: "28px 24px", marginTop: 40 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ fontSize: 11, color: D.muted }}>© 2025 – {new Date().getFullYear()} Cosmoola Technologies</div>
          <div style={{ display: "flex", gap: 20 }}>
            <span onClick={() => navigate("/privacy")} style={{ fontSize: 11, color: D.muted, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Privacy</span>
            <span onClick={() => navigate("/terms")} style={{ fontSize: 11, color: D.muted, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Terms</span>
            <span onClick={() => navigate("/support")} style={{ fontSize: 11, color: D.muted, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Support</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AddMoney;

/* ─── CSS ─── */
const ADD_MONEY_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{background:#fff}button{font-family:inherit}input{font-family:inherit}
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}
  input[type=number]{-moz-appearance:textfield}
  ::-webkit-scrollbar{width:0;height:0}

  .am-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 32px 24px 0;
    display: grid;
    grid-template-columns: 1fr 380px;
    gap: 28px;
    align-items: start;
  }

  .am-main { min-width: 0; }
  .am-summary { min-width: 0; }

  @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  .spin { animation: spin 1s linear infinite; }

  @media (max-width: 900px) {
    .am-container {
      grid-template-columns: 1fr !important;
      gap: 0 !important;
      padding: 20px 16px 0 !important;
    }
    .am-summary {
      position: fixed !important;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 90;
      padding: 0 !important;
    }
    .am-summary > div {
      border: none !important;
      border-top: 1px solid #e5e5e5 !important;
      position: relative !important;
      top: 0 !important;
      box-shadow: 0 -4px 20px rgba(0,0,0,0.08);
    }
    .am-main { padding-bottom: 220px; }
    .back-label { display: none; }
  }

  @media (min-width: 901px) {
    .back-label { display: inline; }
  }
`;